import type { ItemPedido, Preco, PrecoNegociado } from '../db/tipos'

export function arredondar2(valor: number): number {
  return Math.round((valor + Number.EPSILON) * 100) / 100
}

export interface Avulsas {
  quantidade: number
  preco: number
}

// ▶ "Esta venda nao leva unidade solta." Existe como NOME para que a chamada
// que so queria passar o motivo nao escreva um objeto vazio no meio da lista de
// argumentos, e para que "sem avulsas" seja procuravel no fonte.
// Congelado de proposito: e compartilhado, e um objeto compartilhado que alguem
// mutasse vazaria para todas as chamadas de uma vez.
export const SEM_AVULSAS: Readonly<Avulsas> = Object.freeze({ quantidade: 0, preco: 0 })

// ▶ O outro funil (o par de embalagemDoProduto). Item gravado antes da 0021 le
// `undefined` nas duas chaves, e `undefined * 6` e NaN -- que entraria no
// total_item e viraria dinheiro invisivel.
export function avulsasDoItem(
  item: { quantidade_avulsa?: number | null; preco_avulso?: number | null },
): Avulsas {
  return { quantidade: item.quantidade_avulsa ?? 0, preco: item.preco_avulso ?? 0 }
}

// UNICA formula de total de item do sistema -- continua unica, com um termo a
// mais. O 4o parametro e OPCIONAL para que as chamadas que ja existem nao
// mudem de comportamento nem de assinatura.
//
// O arredondamento acontece uma vez, DEPOIS da soma -- partir em dois muda um
// centavo em desconto banal, e ha teste prendendo isso. Com avulsas zeradas o
// resultado e identico ao da formula anterior, e ha teste prendendo isso
// tambem. O 4o parametro nao tem objeto default de proposito: uma constante
// compartilhada so ficaria meio presa (o produto q*p esconde q corrompido
// enquanto p for zero).
export function calcularTotalItem(
  quantidade: number,
  precoUnitario: number,
  descontoPct: number,
  avulsas?: Avulsas,
): number {
  const embalagens = quantidade * precoUnitario
  const soltas = avulsas ? avulsas.quantidade * avulsas.preco : 0
  return arredondar2((embalagens + soltas) * (1 - descontoPct / 100))
}

export function vigente(inicio: string | null, fim: string | null, hoje: string): boolean {
  return (!inicio || inicio <= hoje) && (!fim || fim >= hoje)
}

export interface PrecoResolvido {
  preco: number
  // O preco da unidade SOLTA daquela mesma origem. Nulo = esta tabela (ou este
  // acordo com o cliente) nao vende avulso, que e o caso de tudo que existe
  // hoje. ⚠ `?? null` pelo motivo de sempre: linha em cache antes da 0021 le
  // `undefined`, e `undefined` viraria NaN no total.
  precoUnidade: number | null
  origem: string // 'negociado' | 'tabela:<id>' | 'manual'
}

export function resolverPrecoPuro(
  negociado: PrecoNegociado | undefined,
  precoTabela: Preco | undefined,
  hoje: string,
): PrecoResolvido | null {
  if (
    negociado &&
    negociado.apagado_em === null &&
    vigente(negociado.vigencia_inicio, negociado.vigencia_fim, hoje)
  ) {
    return {
      preco: negociado.preco,
      precoUnidade: negociado.preco_unidade ?? null,
      origem: 'negociado',
    }
  }
  if (precoTabela && precoTabela.apagado_em === null) {
    return {
      preco: precoTabela.preco,
      precoUnidade: precoTabela.preco_unidade ?? null,
      origem: `tabela:${precoTabela.tabela_preco_id}`,
    }
  }
  return null
}

export function dataISOLocal(data: Date): string {
  const ano = data.getFullYear()
  const mes = String(data.getMonth() + 1).padStart(2, '0')
  const dia = String(data.getDate()).padStart(2, '0')
  return `${ano}-${mes}-${dia}`
}

// Como um item do carrinho fica apos a troca de tabela do sub-pedido.
// Sem preco na tabela nova: mantem o preco atual e assume origem 'manual'
// (regra ja existente: produto sem preco exige preco manual).
// O desconto e SEMPRE preservado: e decisao da venda, nao da tabela.
export function reprecificarItem(item: ItemPedido, resolvido: PrecoResolvido | null): ItemPedido {
  const preco = resolvido?.preco ?? item.preco_unitario
  const origem = resolvido?.origem ?? 'manual'
  // O avulso segue a MESMA regra do preco_unitario acima: a tabela nova manda,
  // e quando ela nao vende avulso o item MANTEM o que ja tinha. Zerar aqui
  // apagaria em silencio uma parcela que o vendedor digitou -- e, com a trava
  // da 0021, deixaria "6 avulsas sem preco", estado que o banco recusa.
  const precoAvulso = resolvido?.precoUnidade ?? item.preco_avulso ?? null
  const avulsas = avulsasDoItem({ ...item, preco_avulso: precoAvulso })
  return {
    ...item,
    preco_unitario: preco,
    preco_avulso: precoAvulso,
    origem_preco: origem,
    total_item: calcularTotalItem(item.quantidade, preco, item.desconto_pct, avulsas),
  }
}
